import React, { useMemo } from 'react'
import { Box } from '@hub/box'
import { HubStyleObject } from '@hub/design-system-base'
import { theme } from '@hub/design-system-base/src/theme'
import {
  LocationDropPinMediumIcon,
  PhoneMediumIcon,
  EmailMediumIcon,
  HoursMediumIcon,
  ParkingMediumIcon,
  WebsiteMediumIcon,
  OfferMediumIcon,
} from '@hub/icon'

interface UtilityBoxItemProps {
  icon?: UtilityBoxItemIcons
  children?: React.ReactNode
  role?: string
  'aria-level'?: string
  'aria-label'?: string
  sx?: HubStyleObject
}

export enum UtilityBoxItemIcons {
  LOCATION_DROP_PIN = 'location-drop-pin',
  PHONE = 'phone',
  EMAIL = 'email',
  HOURS = 'hours',
  PARKING = 'parking',
  WEBSITE = 'website',
  OFFER = 'offer',
}

interface ItemIconProps {
  icon: UtilityBoxItemIcons
}

const ItemIcon = ({ icon }: ItemIconProps): React.ReactElement | null => {
  const itemIcon = useMemo(() => {
    switch (icon) {
      case 'location-drop-pin':
        return (
          <LocationDropPinMediumIcon
            boxSize="size-icon-md"
            color={theme.colors.iconPrimary}
          />
        )
      case 'phone':
        return (
          <PhoneMediumIcon
            boxSize="size-icon-md"
            color={theme.colors.iconPrimary}
          />
        )
      case 'email':
        return (
          <EmailMediumIcon
            boxSize="size-icon-md"
            color={theme.colors.iconPrimary}
          />
        )
      case 'hours':
        return (
          <HoursMediumIcon
            boxSize="size-icon-md"
            color={theme.colors.iconPrimary}
          />
        )
      case 'parking':
        return (
          <ParkingMediumIcon
            boxSize="size-icon-md"
            color={theme.colors.iconPrimary}
          />
        )
      case 'website':
        return (
          <WebsiteMediumIcon
            boxSize="size-icon-md"
            color={theme.colors.iconPrimary}
          />
        )
      case 'offer':
        return (
          <OfferMediumIcon
            boxSize="size-icon-md"
            color={theme.colors.iconPrimary}
          />
        )
      default:
        return null
    }
  }, [icon])

  return itemIcon
}

const UtilityBoxItem: React.FunctionComponent<
  React.PropsWithChildren<UtilityBoxItemProps>
> = ({ icon, children, sx, ...restOptions }) => (
  <Box
    sx={{
      paddingInlineStart: 0,
      display: 'flex',
      justifyContent: 'space-between',
      textAlign: 'left',
      marginTop: 'spacing-md',
      alignItems: 'left',
      gap: 'spacing-md',
      maxWidth: 'size-21',
      ...sx,
    }}
    {...restOptions}
  >
    {icon && <ItemIcon icon={icon} />}
    <Box sx={{ flex: 1, maxWidth: 'size-20' }}>{children}</Box>
  </Box>
)

export default UtilityBoxItem
